import { STANDING_TIME_ZONE } from "@/lib/account-standing-copy";
import type { ClosedReason } from "@/lib/account-standing";
import { payerRecipients, type OrgPayer } from "@/lib/account-resolve";

export const ACCOUNT_DATA_RETENTION_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DataRetention {
  closedAt: Date;
  availableUntil: Date;
  deletedOn: Date;
}

export function dataRetention(closedAt: string | null | undefined): DataRetention | null {
  if (!closedAt) return null;
  const closed = new Date(closedAt);
  if (Number.isNaN(closed.getTime())) return null;
  const availableUntil = new Date(closed.getTime() + ACCOUNT_DATA_RETENTION_DAYS * DAY_MS);
  return { closedAt: closed, availableUntil, deletedOn: new Date(availableUntil.getTime() + DAY_MS) };
}

const longDate = new Intl.DateTimeFormat("es-CO", {
  timeZone: STANDING_TIME_ZONE,
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function formatClosureDate(date: Date): string {
  const parts = Object.fromEntries(longDate.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.day} de ${parts.month} de ${parts.year}`;
}

export interface ClosedAccountCopy {
  title: string;
  reason: string;
  retention: string;
  downloadHint: string;
}

export function closedAccountCopy(input: {
  reason: ClosedReason;
  orgName: string | null | undefined;
  closedAt: string | null | undefined;
  isAdmin: boolean;
}): ClosedAccountCopy {
  const account = input.orgName ? `La cuenta ${input.orgName}` : "Tu cuenta";
  const reason = input.reason === "manual_block"
    ? `${account} fue cerrada por nuestro equipo y ya no se puede usar.`
    : `${account} está cerrada y ya no se puede usar.`;
  const retention = dataRetention(input.closedAt);
  const retentionText = retention
    ? `Guardamos tus datos hasta el ${formatClosureDate(retention.availableUntil)}. El ${formatClosureDate(retention.deletedOn)} los borramos de forma definitiva.`
    : `Guardamos tus datos ${ACCOUNT_DATA_RETENTION_DAYS} días desde el cierre. Después los borramos de forma definitiva.`;
  return {
    title: "Tu cuenta está cerrada",
    reason,
    retention: retentionText,
    downloadHint: input.isAdmin
      ? "Descarga tus contactos, conversaciones y demás datos antes de esa fecha."
      : "Solo un administrador de la cuenta puede descargar los datos.",
  };
}

export function closedSupportMessage(orgName: string | null | undefined): string {
  return orgName
    ? `Hola, necesito ayuda con la cuenta cerrada ${orgName}.`
    : "Hola, necesito ayuda con mi cuenta cerrada.";
}

export const CLOSED_ADMINS_TITLE = "Pueden descargarlos";

export function closedAccountMailtoHref(
  admins: OrgPayer[],
  orgName: string | null | undefined,
  closedAt: string | null | undefined,
): string | null {
  const to = payerRecipients(admins);
  if (!to) return null;
  const account = orgName ? `La cuenta ${orgName} de Flow` : "Nuestra cuenta de Flow";
  const retention = dataRetention(closedAt);
  const deadline = retention ? ` antes del ${formatClosureDate(retention.availableUntil)}` : "";
  const subject = orgName ? `La cuenta ${orgName} está cerrada` : "Nuestra cuenta de Flow está cerrada";
  const body = [
    "Hola,",
    "",
    `${account} está cerrada. Solo un administrador puede descargar los contactos, las conversaciones y demás datos${deadline}.`,
    "",
    "Gracias.",
  ].join("\n");
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
